import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { Plus } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import { QuestionToolTip } from '@/components/ui/question-tooltip'
import { ActionCard } from './action-card'
import { Action, PromotionFormData } from './promotion-form'

type ActionType =
    | 'LineItemAdjustment'
    | 'OrderAdjustment'
    | 'ShippingAdjustment'
    | 'CreateLineItem'
    | 'SubscriptionLineItemFreeTrial'
    | 'BOGO'

interface PromotionActionsSectionProps {
    storeId: string
    onAddAction: (type: ActionType) => void
    onUpdateAction: (index: number, updates: Partial<Action>) => void
    onRemoveAction: (index: number) => void
    /** Hide BXGY-style line-item targeting fields (VIP discount context). */
    hideAdvancedTargeting?: boolean
}

export const PromotionActionsSection = ({
    storeId,
    onAddAction,
    onUpdateAction,
    onRemoveAction,
    hideAdvancedTargeting,
}: PromotionActionsSectionProps) => {
    const { t } = useTranslation('stores')
    // `useFormContext().formState` has no own subscription; this re-renders via the parent
    // PromotionForm's `watch('actions')` cascade, which is enough to show/clear the banner below.
    const { watch, formState } = useFormContext<PromotionFormData>()
    const { errors } = formState
    const actions = watch('actions')
    const actionsErrorMessage = (errors.actions as { message?: unknown } | undefined)?.message
    const actionsError = typeof actionsErrorMessage === 'string' ? actionsErrorMessage : undefined

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-1.5">
                            <CardTitle>{t('promotion.promotionActions.title')}</CardTitle>
                            <QuestionToolTip title={t('promotion.promotionActions.titleHelp.title')}>
                                <div className="space-y-4">
                                    <p className="text-muted-foreground">
                                        {t('promotion.promotionActions.titleHelp.intro')}
                                    </p>
                                    <div className="space-y-2">
                                        <p className="font-medium">
                                            {t('promotion.promotionActions.titleHelp.typesTitle')}
                                        </p>
                                        <ul className="text-muted-foreground list-disc space-y-1 pl-4 text-xs">
                                            <li>{t('promotion.promotionActions.titleHelp.typesProductDiscount')}</li>
                                            <li>{t('promotion.promotionActions.titleHelp.typesOrderDiscount')}</li>
                                            <li>{t('promotion.promotionActions.titleHelp.typesFreeShipping')}</li>
                                            <li>{t('promotion.promotionActions.titleHelp.typesAddFreeProduct')}</li>
                                            <li>{t('promotion.promotionActions.titleHelp.typesFreeTrial')}</li>
                                            <li>{t('promotion.promotionActions.titleHelp.typesBogo')}</li>
                                        </ul>
                                    </div>
                                    <div className="space-y-3">
                                        <p className="font-medium">
                                            {t('promotion.promotionActions.titleHelp.examplesTitle')}
                                        </p>
                                        <div>
                                            <p className="text-xs font-medium">
                                                {t('promotion.promotionActions.titleHelp.example1Title')}
                                            </p>
                                            <p className="text-muted-foreground mt-1 text-xs italic">
                                                {t('promotion.promotionActions.titleHelp.example1')}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium">
                                                {t('promotion.promotionActions.titleHelp.example2Title')}
                                            </p>
                                            <p className="text-muted-foreground mt-1 text-xs italic">
                                                {t('promotion.promotionActions.titleHelp.example2')}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium">
                                                {t('promotion.promotionActions.titleHelp.example3Title')}
                                            </p>
                                            <p className="text-muted-foreground mt-1 text-xs italic">
                                                {t('promotion.promotionActions.titleHelp.example3')}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium">
                                                {t('promotion.promotionActions.titleHelp.example4Title')}
                                            </p>
                                            <p className="text-muted-foreground mt-1 text-xs italic">
                                                {t('promotion.promotionActions.titleHelp.example4')}
                                            </p>
                                        </div>
                                    </div>
                                    <p className="text-foreground bg-muted rounded-md p-2 text-xs">
                                        {t('promotion.promotionActions.titleHelp.tip')}
                                    </p>
                                </div>
                            </QuestionToolTip>
                        </div>
                        <p className="text-muted-foreground mt-1 text-sm">{t('promotion.promotionActions.description')}</p>
                    </div>
                    <Select key={actions.length} onValueChange={(value) => onAddAction(value as ActionType)}>
                        <SelectTrigger className="w-[180px]">
                            <Plus className="mr-1 h-3 w-3" />
                            {t('promotion.promotionActions.addAction')}
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="LineItemAdjustment">
                                {t('promotion.promotionActions.types.productDiscount')}
                            </SelectItem>
                            <SelectItem value="OrderAdjustment">{t('promotion.promotionActions.types.orderDiscount')}</SelectItem>
                            <SelectItem value="ShippingAdjustment">
                                {t('promotion.promotionActions.types.freeShipping')}
                            </SelectItem>
                            <SelectItem value="CreateLineItem">
                                {t('promotion.promotionActions.types.addFreeProduct')}
                            </SelectItem>
                            <SelectItem value="SubscriptionLineItemFreeTrial">
                                {t('promotion.promotionActions.types.freeTrial')}
                            </SelectItem>
                            <SelectItem value="BOGO">
                                {t('promotion.promotionActions.types.bogo')}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {actions.length === 0 && actionsError && (
                    <p id="actions" className="mt-1 text-xs text-destructive">{t(actionsError)}</p>
                )}
                {actions.length === 0 && !actionsError && (
                    <p className="text-muted-foreground py-8 text-center text-sm">{t('promotion.promotionActions.noActions')}</p>
                )}
                {actions.map((action, index) => (
                    <ActionCard
                        key={index}
                        action={action}
                        index={index}
                        storeId={storeId}
                        onUpdate={onUpdateAction}
                        onRemove={onRemoveAction}
                        hideAdvancedTargeting={hideAdvancedTargeting}
                    />
                ))}
            </CardContent>
        </Card>
    )
}

